import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Search, Package, Sparkles, Loader2, Edit2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";

export default function Ricambi() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isEn = (i18n.language || "").startsWith("en");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [translatingId, setTranslatingId] = useState(null);
  const [bulkTranslating, setBulkTranslating] = useState(false);
  const [editRow, setEditRow] = useState(null); // spare-part object being edited
  const [editEnText, setEditEnText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const load = useCallback(async (term = "") => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (term) params.q = term;
      const { data } = await api.get("/spare-parts", { params });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSearch = (e) => { e.preventDefault(); load(q.trim()); };

  const translateOne = async (p) => {
    setTranslatingId(p.id);
    try {
      const { data } = await api.post(`/spare-parts/${p.id}/translate`);
      if (data.skipped) toast.info("Già tradotto manualmente");
      else toast.success(`Tradotto: ${data.description_en?.slice(0, 60)}…`);
      await load(q.trim());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTranslatingId(null); }
  };

  const translateAllMissing = async () => {
    if (!window.confirm(
      "Tradurre automaticamente in inglese tutte le descrizioni ancora prive di description_en?\n\n" +
      "L'operazione usa Gemini e può richiedere alcuni secondi in base al numero di ricambi. " +
      "Le descrizioni EN già confermate manualmente non verranno toccate."
    )) return;
    setBulkTranslating(true);
    try {
      const { data } = await api.post("/spare-parts/translate-missing");
      toast.success(`${data.translated} ricambi tradotti`);
      await load(q.trim());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulkTranslating(false); }
  };

  const openEdit = (p) => {
    setEditRow(p);
    setEditEnText(p.description_en || "");
  };

  const saveEdit = async () => {
    if (!editRow) return;
    setSavingEdit(true);
    try {
      await api.put(`/spare-parts/${editRow.id}`, { description_en: editEnText.trim() });
      toast.success("Descrizione EN aggiornata");
      setEditRow(null);
      await load(q.trim());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingEdit(false); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl">
      <PageHeader
        title={isEn ? "Spare parts catalogue" : "Catalogo ricambi"}
        description={isEn ? "List of spare part codes imported from the price list." : "Elenco dei codici ricambio importati dal listino."}
      />

      <form onSubmit={onSearch} className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input
            data-testid="ricambi-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={isEn ? "Search by code or description..." : "Cerca per codice o descrizione..."}
            className="pl-9 rounded-sm h-10"
          />
        </div>
        <Button data-testid="ricambi-search-btn" type="submit" className="bg-brand hover:bg-brand-dark text-white rounded-sm h-10">
          {t("common.search")}
        </Button>
        {q && (
          <Button type="button" variant="outline" className="rounded-sm h-10" onClick={() => { setQ(""); load(""); }}>
            Reset
          </Button>
        )}
        {isAdmin && (
          <Button
            type="button" variant="outline" className="rounded-sm h-10 ml-auto"
            onClick={translateAllMissing} disabled={bulkTranslating}
            data-testid="ricambi-bulk-translate-btn"
            title="Traduce automaticamente in EN tutti i ricambi ancora senza description_en"
          >
            {bulkTranslating ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
            {isEn ? "Auto-translate missing" : "Traduci mancanti"}
          </Button>
        )}
      </form>

      <Card className="rounded-sm border-zinc-200 p-0 overflow-hidden bg-white">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
              <th className="text-left px-5 py-3 font-medium">{isEn ? "Code" : "Codice"}</th>
              <th className="text-left px-5 py-3 font-medium">{isEn ? "Description" : "Descrizione"}</th>
              <th className="text-left px-5 py-3 font-medium">{isEn ? "Compatible models" : "Modelli compatibili"}</th>
              <th className="text-right px-5 py-3 font-medium">{isEn ? "Price" : "Prezzo"}</th>
              {isAdmin && <th className="text-right px-5 py-3 font-medium">{t("common.actions")}</th>}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={isAdmin ? 5 : 4} className="p-10 text-center text-zinc-500">{t("common.loading")}</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={isAdmin ? 5 : 4} className="p-10 text-center text-zinc-500">
                <Package className="h-8 w-8 mx-auto mb-3 text-zinc-300" strokeWidth={1.25} />
                {isEn ? "No spare parts. Import from Settings." : "Nessun ricambio. Importa il listino dalle Impostazioni."}
              </td></tr>
            )}
            {!loading && items.map((p) => (
              <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`ricambio-row-${p.code}`}>
                <td className="px-5 py-3 font-mono text-xs text-zinc-900">{p.code}</td>
                <td className="px-5 py-3 text-zinc-800">
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      {p.description}
                      {isEn && p.description_it && (
                        <div className="text-[11px] text-zinc-400 mt-0.5 italic">{p.description_it}</div>
                      )}
                    </div>
                    {isAdmin && p.description_en_auto && (
                      <span
                        className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-800 border border-amber-200 shrink-0"
                        title="Traduzione automatica AI — clicca l'icona a destra per verificarla"
                      >
                        AI
                      </span>
                    )}
                    {isAdmin && p.description_en && p.description_en_auto === false && (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" title="EN confermata manualmente" />
                    )}
                  </div>
                </td>
                <td className="px-5 py-3 text-xs text-zinc-600">
                  {p.compatible_models?.length ? p.compatible_models.join(", ") : "—"}
                </td>
                <td className="px-5 py-3 text-right font-mono text-zinc-900">
                  {p.price?.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                </td>
                {isAdmin && (
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex gap-1">
                      {!p.description_en && (
                        <button
                          onClick={() => translateOne(p)}
                          disabled={translatingId === p.id}
                          className="inline-flex items-center gap-1 h-7 px-2 rounded-sm border border-zinc-200 text-[11px] text-zinc-700 hover:border-brand hover:text-brand"
                          data-testid={`ricambio-translate-${p.code}`}
                          title="Genera traduzione EN via AI"
                        >
                          {translatingId === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} EN
                        </button>
                      )}
                      <button
                        onClick={() => openEdit(p)}
                        className="inline-flex items-center gap-1 h-7 px-2 rounded-sm border border-zinc-200 text-[11px] text-zinc-700 hover:border-brand hover:text-brand"
                        data-testid={`ricambio-edit-en-${p.code}`}
                        title="Modifica descrizione EN"
                      >
                        <Edit2 className="h-3 w-3" /> EN
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </Card>

      <Dialog open={!!editRow} onOpenChange={(v) => !v && setEditRow(null)}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="ricambio-edit-en-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Descrizione inglese</DialogTitle>
            <DialogDescription>
              <span className="font-mono">{editRow?.code}</span> · IT: {editRow?.description_it || editRow?.description}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-500">English description</Label>
            <Input
              value={editEnText}
              onChange={(e) => setEditEnText(e.target.value)}
              placeholder="e.g. O-ring seal 20x2mm"
              className="rounded-sm"
              data-testid="ricambio-en-input"
            />
            <div className="text-[11px] text-zinc-500">
              Salvando la descrizione verrà segnata come "confermata manualmente" (icona verde),
              non verrà più sovrascritta dalle traduzioni automatiche batch.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setEditRow(null)}>{t("common.cancel")}</Button>
            <Button
              onClick={saveEdit} disabled={savingEdit}
              className="bg-brand hover:bg-brand-dark text-white rounded-sm"
              data-testid="ricambio-en-save"
            >
              {savingEdit ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> {t("common.save")}...</> : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
